import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  VStack,
  useToast,
  Progress,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { updateOrganization } from '../api/authApi';
import { createInvite } from '../api/inviteApi';

const OnboardingPage = () => {
  const { organization, setOrganization } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [workspaceName, setWorkspaceName] = useState(organization?.name || '');
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSaveWorkspace = async () => {
    if (!workspaceName.trim()) return;
    setLoading(true);
    try {
      const updated = await updateOrganization(workspaceName.trim());
      setOrganization(updated);
      setStep(2);
    } catch (err: any) {
      toast({ title: err.response?.data?.message || 'Failed to update workspace', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      navigate('/dashboard');
      return;
    }
    setLoading(true);
    try {
      await createInvite({ email: inviteEmail.trim(), role: 'editor' });
      toast({ title: 'Invite sent!', status: 'success' });
      navigate('/dashboard');
    } catch (err: any) {
      toast({ title: err.response?.data?.message || 'Failed to send invite', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" py={16}>
      <Container maxW="md">
        <VStack spacing={8}>
          <Heading size="lg">Welcome! Let's set up your workspace</Heading>
          <Progress value={step * 50} w="full" colorScheme="blue" borderRadius="full" />

          {step === 1 && (
            <VStack spacing={4} w="full" bg="white" p={8} borderRadius="xl" boxShadow="md">
              <Text color="gray.600">Step 1 of 2 — Name your workspace</Text>
              <FormControl isRequired>
                <FormLabel>Workspace name</FormLabel>
                <Input
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="My Agency"
                  size="lg"
                />
              </FormControl>
              <Button w="full" colorScheme="blue" onClick={handleSaveWorkspace} isLoading={loading}>
                Continue
              </Button>
            </VStack>
          )}

          {step === 2 && (
            <VStack spacing={4} w="full" bg="white" p={8} borderRadius="xl" boxShadow="md">
              <Text color="gray.600">Step 2 of 2 — Invite a teammate (optional)</Text>
              <FormControl>
                <FormLabel>Teammate email</FormLabel>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  size="lg"
                />
              </FormControl>
              <Button w="full" colorScheme="blue" onClick={handleInvite} isLoading={loading}>
                {inviteEmail ? 'Send invite & go to dashboard' : 'Skip & go to dashboard'}
              </Button>
            </VStack>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default OnboardingPage;
